// 단체 통화 화면 (F구역 ⑦ 5단계 4-2c-2)
// - 엔진(groupCallEngine) 상태를 스스로 구독해서 그린다. idle이면 아무것도 그리지 않는다.
// - 참가자 이름·사진은 화면이 직접 불러온다(같은 방 구성원은 DB 열람 규칙 pf_select_related로 허용).
// - 1:1 통화 화면(VoiceCallModal)과는 완전히 별개다. 친구 별명 표시는 숙제(지금은 프로필 이름).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Avatar from './Avatar';
import VideoTile from './VideoTile';
import { supabase } from '../supabaseClient';
import { resolveAvatarUrl } from '../utils/imageUrl';
import { getDisplayName } from '../utils/displayName';
import {
  getGroupCallState,
  subscribeGroupCall,
  subscribeGroupCallEvents,
  acceptIncomingGroupCall,
  dismissIncomingGroupCall,
  hangUpGroupCall,
  toggleGroupCallMute,
  toggleGroupCamera,
  unblockGroupAudio,
  reconnectGroupMic,
} from '../lib/groupCallEngine';
import { setGroupOutputDevice } from '../lib/groupCallMedia';
import { useAudioOutputSupport } from '../hooks/useAudioOutputSupport';
import { useGroupPipDrag } from '../hooks/useGroupPipDrag';
import { useGroupGridSwap } from '../hooks/useGroupGridSwap';

const PROFILE_COLUMNS = 'id, name, nickname, name_display_choice, avatar, avatar_selected';

const PSEUDO_SINK_IDS = new Set(['', 'default', 'communications']);

// 윈도우는 한 기계를 '기본값'·'통신'·본래 이름으로 여러 번 올린다 → groupId로 묶어 실제 기계끼리만 순환
function pickNextOutput(audioOutputs, currentSinkId) {
  const keyOf = (d) => d.groupId || d.deviceId;
  const real = [];
  const seen = new Set();
  for (const d of audioOutputs) {
    if (PSEUDO_SINK_IDS.has(d.deviceId)) continue;
    const k = keyOf(d);
    if (seen.has(k)) continue;
    seen.add(k);
    real.push(d);
  }
  if (real.length < 2) return null;
  const lookupId = currentSinkId || 'default';
  const cur = audioOutputs.find((d) => d.deviceId === lookupId);
  const curKey = cur ? keyOf(cur) : null;
  const idx = curKey ? real.findIndex((d) => keyOf(d) === curKey) : -1;
  return real[(idx + 1) % real.length];
}

function cleanDeviceLabel(label) {
  return String(label || '').replace(/\s*\([0-9a-f]{4}:[0-9a-f]{4}\)\s*$/i, '').trim();
}

const profileCache = new Map(); // id → 프로필. 통화가 끝나도 유지(다음 통화는 기다림 0)
const preloadedAvatarUrls = new Set();
function preloadAvatar(url) {
  if (typeof url === 'string' && url.startsWith('http') && !preloadedAvatarUrls.has(url)) {
    preloadedAvatarUrls.add(url);
    try {
      const img = new Image();
      img.src = url;
    } catch {}
  }
}

// 통화 시간 표시 (MM:SS, 1시간 이상이면 H:MM:SS)
function formatTime(totalSec) {
  const hrs = Math.floor(totalSec / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  if (hrs > 0) {
    return `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

export default function GroupCallModal({ myUserId, showToast }) {
  const { t } = useTranslation();
  const [call, setCall] = useState(() => getGroupCallState());
  const [profiles, setProfiles] = useState(() => Object.fromEntries(profileCache));
  const [_pendingProfileIds, setPendingProfileIds] = useState(() => new Set());
  const [failedProfileIds, setFailedProfileIds] = useState(() => new Set());
  const [elapsed, setElapsed] = useState(0);
  const [aloneSeconds, setAloneSeconds] = useState(0);
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean' ? navigator.onLine : true,
  );
  const [isActing, setIsActing] = useState(false);
  const [isSpeakerToggled, setIsSpeakerToggled] = useState(false);
  const [isSwitchingAudio, setIsSwitchingAudio] = useState(false);
  const [focusedId, setFocusedId] = useState(null);
  const [pressedTileId, setPressedTileId] = useState(null);
  const [showFocusHint, setShowFocusHint] = useState(false);
  const hintShownThisCallRef = useRef(false);
  const containerRef = useRef(null);
  const lastDoubleTapTimeRef = useRef(0);
  const tapTrackRef = useRef({ time: 0, x: 0, y: 0, id: null, downX: 0, downY: 0 });
  const currentSinkIdRef = useRef('');
  const profilesRef = useRef(Object.fromEntries(profileCache));
  const activeSinceRef = useRef({ callId: null, since: null });
  const preloadedCallIdRef = useRef(null);
  const fetchProfilesRef = useRef(null);
  const tRef = useRef(t);
  const showToastRef = useRef(showToast);
  const departBatchRef = useRef({ items: [], timer: null });

  const isVisible = call.phase !== 'idle';
  const isConnected = call.phase === 'active';
  const { outputRole, isSpeakerSupported } = useAudioOutputSupport(isVisible, isConnected);

  useEffect(() => {
    if (call.phase === 'idle') {
      setIsSpeakerToggled(false);
      setIsSwitchingAudio(false);
      currentSinkIdRef.current = '';
      setFocusedId(null);
      hintShownThisCallRef.current = false;
      setShowFocusHint(false);
    }
  }, [call.phase]);

  useEffect(() => {
    tRef.current = t;
    showToastRef.current = showToast;
  }, [t, showToast]);

  // 엔진 상태 구독 (엔진이 상태를 바꿀 때마다 다시 그림)
  useEffect(() => {
    setCall(getGroupCallState());
    return subscribeGroupCall(() => setCall(getGroupCallState()));
  }, []);

  // 브라우저 온라인/오프라인 상태 감지
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const peerIds = useMemo(() => call.peers.map((p) => p.id), [call.peers]);
  const tileIds = useMemo(
    () => [myUserId, ...peerIds.filter((id) => id !== myUserId)].filter(Boolean).slice(0, 4),
    [myUserId, peerIds],
  );

  // [F구역 ⑧-3] 작은 창(PIP) 탭 시 주인공 교체 콜백
  const handlePipTap = useCallback((id) => {
    navigator.vibrate?.(15);
    console.log(`[단체 화상] 주인공 교체 — 대상: ${id.slice(0, 8)}`);
    setFocusedId(id);
    setShowFocusHint(false);
    try {
      localStorage.setItem('moowoo_group_focus_hint', '999');
    } catch {}
  }, []);

  const { positions, resetPositions, getPipHandlers } = useGroupPipDrag({
    containerRef,
    onTap: handlePipTap,
  });

  // [F구역 ⑧-4] 격자 칸 길게 눌러 끌어서 맞바꾸기 훅
  const { getSlotIndex, getSwapHandlers, draggingId, dropTargetId, dragOffset, isDragActive, cancelLongPress, resetOrder } = useGroupGridSwap({
    tileIds,
    enabled: Boolean(!focusedId || !tileIds.includes(focusedId)),
    containerRef,
    onDragStart: () => {
      setPressedTileId(null);
      tapTrackRef.current = { time: 0, x: 0, y: 0, id: null, downX: 0, downY: 0 };
    },
  });

  useEffect(() => { resetOrder(); }, [call.id, resetOrder]);

  // 주인공 전환 또는 모드 변경 시 작은 창 좌표 초기화
  useEffect(() => {
    resetPositions();
  }, [focusedId, resetPositions]);

  // 주인공 퇴장 또는 혼자 남았을 때 자동으로 격자 복귀
  useEffect(() => {
    if (!focusedId) return;
    if (!tileIds.includes(focusedId)) {
      navigator.vibrate?.(15);
      console.log('[단체 화상] 격자로 복귀 — 사유: 주인공 퇴장');
      setFocusedId(null);
    } else if (tileIds.length <= 1) {
      navigator.vibrate?.(15);
      console.log('[단체 화상] 격자로 복귀 — 사유: 혼자 남음');
      setFocusedId(null);
    }
  }, [focusedId, tileIds]);

  // [F구역 ⑧-3 보완] 2인 이상 시 "두 번 탭하면 크게 볼 수 있어요" 안내 (최대 3회, 2초 후 사라짐)
  useEffect(() => {
    if (tileIds.length >= 2 && !hintShownThisCallRef.current && !focusedId) {
      let count = 0;
      try {
        count = parseInt(localStorage.getItem('moowoo_group_focus_hint') || '0', 10);
      } catch {}
      if (count < 3) {
        hintShownThisCallRef.current = true;
        setShowFocusHint(true);
        try {
          localStorage.setItem('moowoo_group_focus_hint', String(count + 1));
        } catch {}
        const timer = setTimeout(() => {
          setShowFocusHint(false);
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [tileIds.length, focusedId]);

  // 격자 칸 및 주인공 화면 두 번 탭(두 번째 터치다운 시 즉시 발동) 및 누름 효과
  const handleTilePointerDown = useCallback((id, e) => {
    if (!e.isPrimary || e.button !== 0) return;
    if (e.target.closest('button')) return;
    setPressedTileId(id);

    const now = performance.now();
    const prev = tapTrackRef.current;
    const timeDiff = now - prev.time;
    const dist = Math.hypot(e.clientX - prev.x, e.clientY - prev.y);

    if (prev.id === id && timeDiff >= 40 && timeDiff <= 350 && dist <= 24) {
      // 두 번째 손이 닿는 순간 즉시 발동
      cancelLongPress();
      tapTrackRef.current = { time: 0, x: 0, y: 0, id: null, downX: 0, downY: 0 };
      lastDoubleTapTimeRef.current = now;
      navigator.vibrate?.(15);
      if (!focusedId) {
        if (tileIds.length >= 2) {
          console.log(`[단체 화상] 크게 보기 — 대상: ${id.slice(0, 8)}`);
          setFocusedId(id);
          setShowFocusHint(false);
          try {
            localStorage.setItem('moowoo_group_focus_hint', '999');
          } catch {}
        }
      } else if (focusedId === id) {
        console.log('[단체 화상] 격자로 복귀 — 사유: 두 번 탭');
        setFocusedId(null);
      }
    } else {
      tapTrackRef.current.downX = e.clientX;
      tapTrackRef.current.downY = e.clientY;
    }
  }, [cancelLongPress, focusedId, tileIds.length]);

  const handleTilePointerUp = useCallback((id, e) => {
    setPressedTileId(null);
    if (!e.isPrimary) return;
    if (e.target.closest('button')) return;
    if (isDragActive()) {
      tapTrackRef.current = { time: 0, x: 0, y: 0, id: null, downX: 0, downY: 0 };
      console.log('[단체 화상] 끌기 끝 손 뗌 — 탭으로 세지 않음');
      return;
    }

    const dx = Math.abs(e.clientX - tapTrackRef.current.downX);
    const dy = Math.abs(e.clientY - tapTrackRef.current.downY);
    if (Math.hypot(dx, dy) > 8) return;

    const now = performance.now();
    tapTrackRef.current = {
      time: now,
      x: e.clientX,
      y: e.clientY,
      id,
      downX: tapTrackRef.current.downX,
      downY: tapTrackRef.current.downY,
    };
  }, [isDragActive]);

  const handleTileDoubleClick = useCallback((id, e) => {
    if (e.target.closest('button')) return;
    if (isDragActive()) return;
    const now = performance.now();
    if (now - lastDoubleTapTimeRef.current < 400) return;
    lastDoubleTapTimeRef.current = now;
    navigator.vibrate?.(15);

    if (!focusedId) {
      if (tileIds.length >= 2) {
        console.log(`[단체 화상] 크게 보기 — 대상: ${id.slice(0, 8)}`);
        setFocusedId(id);
        setShowFocusHint(false);
        try {
          localStorage.setItem('moowoo_group_focus_hint', '999');
        } catch {}
      }
    } else if (focusedId === id) {
      console.log('[단체 화상] 격자로 복귀 — 사유: 두 번 탭');
      setFocusedId(null);
    }
  }, [focusedId, tileIds.length, isDragActive]);

  // 이름·사진이 필요한 사람 목록 (나 + 건 사람 + 연결된 상대들)
  const idsKey = useMemo(() => {
    const set = new Set();
    if (myUserId) set.add(myUserId);
    if (call.callerId) set.add(call.callerId);
    peerIds.forEach((id) => set.add(id));
    return Array.from(set).sort().join(',');
  }, [myUserId, call.callerId, peerIds]);

  // 프로필 조회 통합 함수: 캐시 먼저 적용 후 누락분만 서버 요청
  const fetchProfiles = useCallback(async (ids) => {
    if (!ids || ids.length === 0) return;
    const targetIds = Array.from(new Set(ids.filter(Boolean)));
    const needed = [];
    const cachedToApply = {};
    let hasCached = false;

    targetIds.forEach((id) => {
      if (profileCache.has(id)) {
        if (!profilesRef.current[id]) {
          cachedToApply[id] = profileCache.get(id);
          hasCached = true;
        }
      } else {
        needed.push(id);
      }
    });

    if (hasCached) {
      const next = { ...profilesRef.current, ...cachedToApply };
      profilesRef.current = next;
      setProfiles(next);
    }

    if (needed.length === 0) return;

    setPendingProfileIds((prev) => new Set([...prev, ...needed]));
    setFailedProfileIds((prev) => {
      if (!needed.some((id) => prev.has(id))) return prev;
      const next = new Set(prev);
      needed.forEach((id) => next.delete(id));
      return next;
    });

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select(PROFILE_COLUMNS)
        .in('id', needed);

      if (error) {
        console.warn('[GroupCallModal] 참가자 프로필 불러오기 실패:', error);
        return;
      }

      if (Array.isArray(data) && data.length > 0) {
        const next = { ...profilesRef.current };
        data.forEach((p) => {
          if (p?.id) {
            profileCache.set(p.id, p);
            next[p.id] = p;
            if (p.avatar_selected && p.avatar) {
              const url = resolveAvatarUrl(p.avatar);
              preloadAvatar(url);
            }
          }
        });
        profilesRef.current = next;
        setProfiles(next);
      }
    } catch (e) {
      console.warn('[GroupCallModal] 참가자 프로필 불러오기 오류:', e);
    } finally {
      const missed = needed.filter((id) => !profilesRef.current[id]);
      if (missed.length > 0) {
        setFailedProfileIds((prev) => new Set([...prev, ...missed]));
      }
      setPendingProfileIds((prev) => {
        const next = new Set(prev);
        needed.forEach((id) => next.delete(id));
        return next;
      });
    }
  }, []);

  fetchProfilesRef.current = fetchProfiles;

  // 아직 모르는 사람만 프로필을 불러온다 (상시 유지)
  useEffect(() => {
    if (call.phase === 'idle' || !idsKey) return;
    const ids = idsKey.split(',').filter(Boolean);
    void fetchProfiles(ids);
  }, [call.phase, idsKey, fetchProfiles]);

  // 벨 울림(incoming)·발신(outgoing) 단계에서 그 방 활성 참가자 프로필을 미리 받아 둔다
  useEffect(() => {
    if (!call.callId || !call.conversationId) return;
    if (call.phase !== 'incoming' && call.phase !== 'outgoing' && call.phase !== 'active') return;
    if (preloadedCallIdRef.current === call.callId) return;
    preloadedCallIdRef.current = call.callId;

    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('participants')
          .select('profile_id')
          .eq('conversation_id', call.conversationId)
          .eq('status', 'active');

        if (cancelled) return;
        if (error) {
          console.warn('[단체 통화] 프로필 미리 받기 실패 —', error);
          return;
        }

        const ids = new Set();
        if (Array.isArray(data)) {
          data.forEach((row) => {
            if (row.profile_id) ids.add(row.profile_id);
          });
        }
        if (call.callerId) ids.add(call.callerId);
        if (myUserId) ids.add(myUserId);

        await fetchProfiles(Array.from(ids));
      } catch (err) {
        if (!cancelled) {
          console.warn('[단체 통화] 프로필 미리 받기 실패 —', err);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [call.callId, call.conversationId, call.phase, call.callerId, myUserId, fetchProfiles]);

  // S-6: 통화 시간 — phase가 처음 'active'가 된 시각을 callId별로 기억해 1초마다 갱신
  useEffect(() => {
    if (call.phase === 'idle' || !call.callId) {
      activeSinceRef.current = { callId: null, since: null };
      setElapsed(0);
      return undefined;
    }
    if (call.phase !== 'active') {
      setElapsed(0);
      return undefined;
    }
    if (activeSinceRef.current.callId !== call.callId || !activeSinceRef.current.since) {
      activeSinceRef.current = { callId: call.callId, since: Date.now() };
    }
    const tick = () => {
      const since = activeSinceRef.current.since || Date.now();
      setElapsed(Math.max(0, Math.floor((Date.now() - since) / 1000)));
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [call.phase, call.callId]);

  // S-5 ②: 혼자 남음 카운트다운 (250ms 간격, 남은 초 올림, 최소 0)
  useEffect(() => {
    if (!call.aloneDeadline) {
      setAloneSeconds(0);
      return undefined;
    }
    const updateSeconds = () => {
      const remain = Math.max(0, Math.ceil((call.aloneDeadline - Date.now()) / 1000));
      setAloneSeconds(remain);
    };
    updateSeconds();
    const timer = setInterval(updateSeconds, 250);
    return () => clearInterval(timer);
  }, [call.aloneDeadline]);

  // S-9 & S-10: 엔진 사건 구독 및 토스트 표시
  useEffect(() => {
    const clearDepartBatch = () => {
      if (departBatchRef.current.timer) {
        clearTimeout(departBatchRef.current.timer);
        departBatchRef.current.timer = null;
      }
      departBatchRef.current.items = [];
    };

    const ensureProfileName = async (userId) => {
      const fallback = tRef.current('chat_conversation.header_unknown_user');
      if (!userId) return fallback;
      if (profilesRef.current[userId]) {
        return getDisplayName(profilesRef.current[userId], fallback);
      }
      if (profileCache.has(userId)) {
        const p = profileCache.get(userId);
        const next = { ...profilesRef.current, [userId]: p };
        profilesRef.current = next;
        setProfiles(next);
        return getDisplayName(p, fallback);
      }
      try {
        await Promise.race([
          fetchProfilesRef.current?.([userId]),
          new Promise((resolve) => setTimeout(resolve, 1000)),
        ]);
        if (profilesRef.current[userId]) {
          return getDisplayName(profilesRef.current[userId], fallback);
        }
      } catch {
        // 실패 시 기본 문구 사용
      }
      return getDisplayName(profilesRef.current[userId], fallback);
    };

    const flushDepartBatch = async () => {
      const items = departBatchRef.current.items.slice();
      departBatchRef.current.items = [];
      departBatchRef.current.timer = null;
      if (items.length === 0) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      const first = items[0];
      const name = await ensureProfileName(first.peerId);
      if (typeof document !== 'undefined' && document.hidden) return;
      if (items.length === 1) {
        const key = first.type === 'peer_lost' ? 'call.group_peer_lost' : 'call.group_peer_left';
        showToastRef.current?.(tRef.current(key, { name }), 2500);
      } else {
        showToastRef.current?.(
          tRef.current('call.group_peers_left', { name, count: items.length - 1 }),
          2500,
        );
      }
    };

    const unsub = subscribeGroupCallEvents((ev) => {
      if (!ev || !ev.type) return;
      if (ev.type === 'camera_error') {
        const isDenied = ev.error === 'NotAllowedError';
        const isDesktop = typeof window !== 'undefined' && !('ontouchstart' in window);
        const key = isDenied
          ? (isDesktop ? 'call.camera_permission_denied_desktop' : 'call.camera_permission_denied')
          : 'call.camera_unavailable';
        showToastRef.current?.(tRef.current(key), 3000);
        return;
      }
      if (ev.type === 'call_ended') {
        clearDepartBatch();
        const reason = ev.reason;
        if (reason === '응답 없음') {
          showToastRef.current?.(tRef.current('call.group_no_answer'), 2500);
        } else if (reason === '혼자 남음') {
          showToastRef.current?.(tRef.current('call.group_ended_alone'), 2500);
        } else if (reason === '자리 비움 퇴장') {
          showToastRef.current?.(tRef.current('call.group_left_while_away'), 2500);
        } else if (reason === '건 사람 취소') {
          showToastRef.current?.(tRef.current('call.group_cancelled'), 2500);
        } else if (reason === 'mesh 실패') {
          showToastRef.current?.(tRef.current('call.failed'), 2500);
        }
        return;
      }

      if (typeof document !== 'undefined' && document.hidden) return;

      if (ev.type === 'peer_joined' && ev.peerId) {
        void (async () => {
          const name = await ensureProfileName(ev.peerId);
          if (typeof document !== 'undefined' && document.hidden) return;
          showToastRef.current?.(tRef.current('call.group_peer_joined', { name }), 2500);
        })();
        return;
      }

      if ((ev.type === 'peer_left' || ev.type === 'peer_lost') && ev.peerId) {
        const exists = departBatchRef.current.items.some((item) => item.peerId === ev.peerId);
        if (!exists) {
          departBatchRef.current.items.push({ type: ev.type, peerId: ev.peerId });
        }
        if (!departBatchRef.current.timer) {
          departBatchRef.current.timer = setTimeout(() => {
            void flushDepartBatch();
          }, 1500);
        }
      }
    });

    return () => {
      unsub();
      clearDepartBatch();
    };
  }, []);

  const [isMicGuideOpen, setIsMicGuideOpen] = useState(false);
  useEffect(() => {
    if (call.phase !== 'active') setIsMicGuideOpen(false); // 통화가 끝나면 안내창도 닫아 다음 통화에 남지 않게
  }, [call.phase]);

  if (call.phase === 'idle') return null;

  const unknownName = t('chat_conversation.header_unknown_user');
  const realNameOf = (id) => getDisplayName(profiles[id], unknownName);
  const labelOf = (id) => (id && id === myUserId ? t('call.group_me') : realNameOf(id));
  const peerStateOf = (id) => call.peers.find((p) => p.id === id)?.state;

  // S-11: DB/마이크 결과 문자열에 맞춘 안내 (가득 참 / 이미 끝남 / 마이크 권한 / 그 밖)
  const messageForFailure = (res) => {
    const reason = String(res?.reason ?? res?.error ?? '');
    if (reason.includes('call_full')) return t('call.group_call_full');
    if (reason.includes('call_not_active')) return t('call.already_ended');
    if (reason === 'mic' || reason.includes('mic')) return t('call.mic_permission_denied');
    return t('call.failed');
  };

  const handleAccept = async () => {
    if (isActing) return;
    setIsActing(true);
    try {
      const res = await acceptIncomingGroupCall();
      if (res && res.ok === false) showToast?.(messageForFailure(res), 2500);
    } catch (e) {
      console.warn('[GroupCallModal] 받기 오류:', e);
      showToast?.(t('call.failed'), 2500);
    } finally {
      setIsActing(false);
    }
  };

  const handleDecline = () => {
    dismissIncomingGroupCall();
  };

  const handleHangUp = () => {
    Promise.resolve(hangUpGroupCall('내가 끊음')).catch((e) => console.warn('[GroupCallModal] 끊기 오류:', e));
  };

  const handleToggleMute = () => {
    toggleGroupCallMute();
  };

  const handleToggleCamera = () => {
    void toggleGroupCamera();
  };

  // ★3 단체 통화 통역 버튼 자리 — 기능 준비 전이라 1:1 통화와 같은 안내 토스트만 띄운다
  const handleTranslateClick = () => {
    showToast?.(t('call.feature_in_verification'), 2000);
  };

  const handleSpeakerClick = async () => {
    if (isSwitchingAudio) return;
    if (outputRole !== 'speaker' || !isSpeakerSupported) {
      showToast?.(t('call.speaker_not_supported'), 3000);
      return;
    }
    setIsSwitchingAudio(true);
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioOutputs = devices.filter((d) => d.kind === 'audiooutput');
      const firstAudio =
        typeof document !== 'undefined' ? document.querySelector('audio[data-group-peer]') : null;
      const currentSinkId = firstAudio?.sinkId || currentSinkIdRef.current || '';
      const nextDevice = pickNextOutput(audioOutputs, currentSinkId);
      if (!nextDevice) return;
      await setGroupOutputDevice(nextDevice.deviceId);
      currentSinkIdRef.current = nextDevice.deviceId;
      setIsSpeakerToggled((prev) => !prev);
      const deviceName = cleanDeviceLabel(nextDevice.label);
      const successMsg = deviceName
        ? t('call.speaker_switched_named', { device: deviceName })
        : t('call.speaker_switched');
      showToast?.(successMsg, 3000);
    } catch (err) {
      console.warn('[단체 통화] 스피커 출력 전환 실패:', err);
      showToast?.(t('call.speaker_switch_failed'), 3000);
    } finally {
      setIsSwitchingAudio(false);
    }
  };

  const handleReconnectAudioClick = async () => {
    if (isSwitchingAudio) return;
    setIsSwitchingAudio(true);
    try {
      const res = await reconnectGroupMic();
      if (res?.ok) {
        const label = String(res.label || '').slice(0, 40);
        showToast?.(
          label ? t('call.mic_reconnected_named', { name: label }) : t('call.device_reconnected'),
          3000,
        );
      } else if (res?.reason !== 'busy') {
        showToast?.(t('call.device_reconnect_failed'), 3000);
      }
    } catch (err) {
      console.warn('[단체 통화] 이어폰 다시 잡기 오류:', err);
      showToast?.(t('call.device_reconnect_failed'), 3000);
    } finally {
      setIsSwitchingAudio(false);
    }
  };

  const handleUnblock = () => {
    Promise.resolve(unblockGroupAudio()).catch((e) => console.warn('[GroupCallModal] 소리 켜기 오류:', e));
  };

  const overlayClass =
    'fixed inset-0 z-[100] flex overflow-y-auto bg-black/60 backdrop-blur-sm select-none px-3 pt-[calc(env(safe-area-inset-top,0px)+12px)] pb-[calc(env(safe-area-inset-bottom,0px)+12px)]';
  const cardClass =
    'relative m-auto w-full max-w-[400px] h-[min(80dvh,720px)] min-h-[460px] bg-[#fbf9f1] text-[#1b1c17] rounded-3xl border-2 border-[#006565] shadow-2xl p-4 sm:p-5 flex flex-col animate-scaleIn overflow-hidden';
  const isVideoKind = call.kind === 'video';
  const callTitle = t(isVideoKind ? 'call.group_call_title_video' : 'call.group_call_title');

  // ── 전화가 왔을 때 ──
  if (call.phase === 'incoming') {
    const callerId = call.callerId;
    const isLoadingCaller = Boolean(callerId) && !profiles[callerId] && !failedProfileIds.has(callerId);
    return (
      <div className={overlayClass}>
        <div className={cardClass}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-base font-semibold text-[#006565]">{callTitle}</span>
            <span className="text-sm text-[#3e4949]">{t(isVideoKind ? 'call.group_incoming_video' : 'call.group_incoming')}</span>
          </div>
          <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-4">
            {isLoadingCaller ? (
              <div className="w-24 h-24 rounded-full bg-[#006565]/10" aria-hidden="true" />
            ) : (
              <Avatar
                userId={callerId}
                name={realNameOf(callerId)}
                avatarUrl={resolveAvatarUrl(profiles[callerId]?.avatar)}
                avatarSelected={profiles[callerId]?.avatar_selected ?? false}
                size="w-24 h-24"
              />
            )}
            <span className="text-xl font-semibold text-[#1b1c17] truncate max-w-full">
              {isLoadingCaller ? '\u00A0' : realNameOf(callerId)}
            </span>
          </div>
          <div className="mt-4 flex items-start justify-around">
            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={handleDecline}
                className="w-16 h-16 rounded-full flex items-center justify-center bg-[#ba1a1a] hover:bg-[#93000a] text-white active:scale-95 transition-all cursor-pointer"
                aria-label={t('call.decline')}
              >
                <span className="material-symbols-outlined text-[30px]">call_end</span>
              </button>
              <span className="text-xs text-[#3e4949]">{t('call.decline')}</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={handleAccept}
                disabled={isActing}
                className="w-16 h-16 rounded-full flex items-center justify-center bg-[#1b8a3a] hover:bg-[#156d2e] text-white active:scale-95 transition-all cursor-pointer disabled:opacity-50"
                aria-label={t('call.accept')}
              >
                <span className="material-symbols-outlined text-[30px]">call</span>
              </button>
              <span className="text-xs text-[#3e4949]">{t('call.accept')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── 거는 중 / 연결 중 / 통화 중 ──
  const count = tileIds.length;
  const isFocusedMode = Boolean(focusedId && tileIds.includes(focusedId) && count >= 2);
  const pipIds = isFocusedMode ? tileIds.filter((id) => id !== focusedId) : [];
  let gridLayout = 'grid-cols-1 grid-rows-1';
  if (count === 2) gridLayout = 'grid-cols-1 grid-rows-2';
  else if (count >= 3) gridLayout = 'grid-cols-2 grid-rows-2';
  const avatarSize = count <= 2 ? 'w-20 h-20' : 'w-16 h-16';

  let statusText = t('call.group_connecting');
  if (call.phase === 'outgoing') statusText = t('call.group_waiting');

  const allPeersDisconnected =
    call.peers.length >= 1 && call.peers.every((p) => p.state === 'disconnected');
  const isUnstable = !isOnline || allPeersDisconnected;

  let bannerNode = null;
  if (call.aloneDeadline) {
    bannerNode = (
      <div className="mt-3 px-3 py-2 rounded-xl text-[13px] text-center bg-[#006565]/10 text-[#006565] font-medium">
        {t('call.group_alone_countdown', { seconds: aloneSeconds })}
      </div>
    );
  } else if (isUnstable) {
    bannerNode = (
      <div className="mt-3 px-3 py-2 rounded-xl text-[13px] text-center bg-[#E67E22]/15 text-[#8a4a10] font-medium">
        {t('call.group_unstable')}
      </div>
    );
  }

  return (
    <div className={overlayClass}>
      {call.audioBlocked && (
        <div className="fixed top-[calc(env(safe-area-inset-top,0px)+72px)] left-1/2 -translate-x-1/2 z-[101] animate-fadeIn pointer-events-auto">
          <button
            type="button"
            onClick={handleUnblock}
            className="px-4 py-2 rounded-full bg-[#006565] text-white text-[13px] font-semibold shadow-xl border border-white/30 flex items-center gap-1.5 hover:bg-[#005050] active:scale-95 transition-all cursor-pointer"
          >
            <span>🔊</span>
            <span>{t('call.tap_to_unmute', '눌러서 소리 켜기')}</span>
          </button>
        </div>
      )}
      <div className={cardClass}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-base font-semibold text-[#006565]">{callTitle}</span>
          <span className="text-sm font-medium text-[#006565] tabular-nums">
            {call.phase === 'active' ? formatTime(elapsed) : statusText}
          </span>
        </div>

        <div className="mt-2 flex justify-center">
          <span className="bg-[#006565]/10 text-[#006565] text-[14px] px-3.5 py-0.5 rounded-full">
            {t('call.group_participants', { count: peerIds.length + 1 })}
          </span>
        </div>

        {call.phase === 'active' && call.isMicLocked && !isMicGuideOpen && (
          <div className="mt-3 px-3 py-2 rounded-xl bg-[#ba1a1a] text-white flex items-center justify-between gap-2 text-left">
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <span className="material-symbols-outlined text-[17px] flex-shrink-0 text-white animate-pulse">
                mic_off
              </span>
              <span className="text-[12px] font-medium leading-snug text-white line-clamp-2">
                {t('call.mic_locked_banner')}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setIsMicGuideOpen(true)}
              className="flex-shrink-0 text-[11px] font-bold bg-white/25 hover:bg-white/35 text-white px-2.5 py-1 rounded-full active:scale-95 transition-all whitespace-nowrap cursor-pointer"
            >
              {t('call.mic_locked_help_btn')}
            </button>
          </div>
        )}

        {bannerNode}

        <div
          ref={containerRef}
          className={`relative flex-1 min-h-0 mt-3 overflow-hidden select-none touch-manipulation ${
            isFocusedMode ? '' : `grid gap-3 ${gridLayout}`
          }`}
        >
          {tileIds.map((id, idx) => {
            void idx;
            const isSpeaking =
              Array.isArray(call.speakingIds) &&
              call.speakingIds.includes(id) &&
              (id !== myUserId || !call.isMuted);
            const isLoadingTile = Boolean(id) && !profiles[id] && !failedProfileIds.has(id);
            const isVideoTile =
              !isLoadingTile &&
              (id === myUserId
                ? Boolean(call.cameraOn && call.localVideoStream)
                : Boolean(call.remoteVideoStreams?.[id] && call.remoteCameraOn?.[id]));
            const isPipTile = isFocusedMode && id !== focusedId;
            const isFocusedTile = isFocusedMode && id === focusedId;

            const isPressed = pressedTileId === id;
            const pressClasses = isPressed ? 'scale-[0.97] brightness-90 motion-reduce:scale-100' : '';
            const slotIndex = getSlotIndex(id);

            let tileClasses = '';
            if (isPipTile) {
              tileClasses = `absolute z-20 w-[76px] h-[100px] rounded-xl shadow-lg border overflow-hidden touch-none flex flex-col items-center justify-center bg-[#f0eee6] cursor-grab active:cursor-grabbing border-white/40 ${
                isSpeaking
                  ? 'ring-2 ring-inset ring-[#006565] animate-[moowoo-speak_1.6s_ease-in-out_infinite] motion-reduce:animate-none'
                  : ''
              }`;
            } else if (isFocusedTile) {
              tileClasses = `absolute inset-0 z-0 overflow-hidden flex flex-col items-center justify-center rounded-2xl bg-[#f0eee6] p-3 border-2 border-transparent transition-all duration-100 ${pressClasses} ${
                isSpeaking
                  ? 'ring-[3px] ring-inset ring-[#006565] animate-[moowoo-speak_1.6s_ease-in-out_infinite] motion-reduce:animate-none'
                  : ''
              }`;
            } else {
              tileClasses = `relative overflow-hidden min-h-0 h-full flex flex-col items-center justify-center gap-2 rounded-2xl bg-[#f0eee6] p-3 border-2 border-transparent transition-all duration-100 select-none touch-none [-webkit-touch-callout:none] ${pressClasses} ${
                isSpeaking
                  ? 'ring-[3px] ring-inset ring-[#006565] animate-[moowoo-speak_1.6s_ease-in-out_infinite] motion-reduce:animate-none'
                  : ''
              } ${count === 3 && slotIndex === 0 ? 'col-span-2' : ''}`;
            }

            const pipIndex = isPipTile ? pipIds.indexOf(id) : -1;
            const pipPos = isPipTile ? positions[id] : null;
            const isDraggingTile = !isFocusedMode && draggingId === id;
            const isDropTargetTile = !isFocusedMode && dropTargetId === id;

            let tileStyle = undefined;
            if (isPipTile) {
              tileStyle = pipPos
                ? { left: `${pipPos.left}px`, top: `${pipPos.top}px`, right: 'auto' }
                : { top: '12px', right: `${12 + Math.max(0, pipIndex) * 84}px` };
            } else if (!isFocusedMode) {
              if (isDraggingTile) {
                tileStyle = {
                  order: slotIndex,
                  transform: `translate3d(${dragOffset.x}px, ${dragOffset.y}px, 0) scale(1.03)`,
                  zIndex: 30,
                  boxShadow: '0 12px 28px rgba(0,0,0,0.35)',
                  opacity: 0.92,
                  transition: 'none',
                };
              } else if (isDropTargetTile) {
                tileStyle = { order: slotIndex, outline: '2px dashed #006565', outlineOffset: '-4px' };
              } else {
                tileStyle = { order: slotIndex };
              }
            }

            const swapHandlers = getSwapHandlers(id);
            const tileHandlers = isPipTile
              ? getPipHandlers(id)
              : {
                  onPointerDown: (e) => { handleTilePointerDown(id, e); swapHandlers.onPointerDown(e); },
                  onPointerMove: swapHandlers.onPointerMove,
                  onPointerUp: (e) => { handleTilePointerUp(id, e); swapHandlers.onPointerUp(e); },
                  onPointerCancel: (e) => { setPressedTileId(null); swapHandlers.onPointerCancel(e); },
                  onPointerLeave: (e) => { if (!draggingId) setPressedTileId(null); swapHandlers.onPointerLeave(e); },
                  onDoubleClick: (e) => handleTileDoubleClick(id, e),
                  onContextMenu: (e) => e.preventDefault(),
                  onDragStart: (e) => e.preventDefault(),
                };

            return (
              <div
                key={id}
                data-tile-id={id}
                className={tileClasses}
                style={tileStyle}
                {...tileHandlers}
              >
                {isLoadingTile ? (
                  <div
                    className={`${isPipTile ? 'w-10 h-10' : avatarSize} rounded-full bg-[#006565]/10`}
                    aria-hidden="true"
                  />
                ) : isVideoTile ? (
                  <VideoTile
                    stream={id === myUserId ? call.localVideoStream : call.remoteVideoStreams[id]}
                    mirrored={id === myUserId}
                    className={`absolute inset-0 w-full h-full ${isPipTile ? 'rounded-xl' : 'rounded-2xl'}`}
                  />
                ) : (
                  <Avatar
                    userId={id}
                    name={realNameOf(id)}
                    avatarUrl={resolveAvatarUrl(profiles[id]?.avatar)}
                    avatarSelected={profiles[id]?.avatar_selected ?? false}
                    size={isPipTile ? 'w-10 h-10' : avatarSize}
                  />
                )}
                <div
                  className={
                    isPipTile
                      ? isVideoTile
                        ? 'absolute left-1.5 bottom-1 flex items-center gap-0.5 max-w-[90%] pointer-events-none'
                        : 'contents'
                      : isVideoTile
                        ? 'absolute left-2.5 bottom-2 flex items-center gap-1 max-w-[85%] pointer-events-none'
                        : 'contents'
                  }
                >
                  <span
                    className={
                      isPipTile
                        ? isVideoTile
                          ? 'text-[10px] font-medium truncate text-white [text-shadow:0_0_2px_rgba(0,0,0,0.9),0_1px_4px_rgba(0,0,0,0.75)]'
                          : 'text-[10px] font-medium truncate max-w-full text-[#1b1c17]'
                        : isVideoTile
                          ? 'text-[13px] font-medium truncate text-white [text-shadow:0_0_2px_rgba(0,0,0,0.9),0_1px_4px_rgba(0,0,0,0.75)]'
                          : 'text-sm font-medium truncate max-w-full text-[#1b1c17]'
                    }
                  >
                    {isLoadingTile ? '\u00A0' : labelOf(id)}
                  </span>
                  {(id === myUserId ? call.isMuted : (call.mutedPeerIds || []).includes(id)) && (
                    <span
                      className={`material-symbols-outlined ${isPipTile ? 'text-[13px]' : 'text-[18px]'} ${
                        isVideoTile
                          ? 'text-white [text-shadow:0_0_2px_rgba(0,0,0,0.9),0_1px_4px_rgba(0,0,0,0.75)]'
                          : 'text-[#ba1a1a]'
                      }`}
                    >
                      mic_off
                    </span>
                  )}
                  {id !== myUserId && peerStateOf(id) !== 'connected' && (
                    <span
                      className={
                        isPipTile
                          ? isVideoTile
                            ? 'text-[9px] whitespace-nowrap text-white [text-shadow:0_0_2px_rgba(0,0,0,0.9),0_1px_4px_rgba(0,0,0,0.75)]'
                            : 'text-[9px] text-[#6e7979]'
                          : isVideoTile
                            ? 'text-[11px] whitespace-nowrap text-white [text-shadow:0_0_2px_rgba(0,0,0,0.9),0_1px_4px_rgba(0,0,0,0.75)]'
                            : 'text-[11px] text-[#6e7979]'
                      }
                    >
                      {t('call.group_peer_connecting')}
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          {isFocusedMode && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigator.vibrate?.(15);
                console.log('[단체 화상] 격자로 복귀 — 사유: 버튼');
                setFocusedId(null);
              }}
              aria-label={t('call.back_to_grid', '격자로 돌아가기')}
              className="absolute top-3 left-3 z-30 w-10 h-10 flex items-center justify-center active:scale-95 cursor-pointer -ml-0.5 -mt-0.5"
            >
              <span className="w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm flex items-center justify-center text-white shadow-md">
                <span className="material-symbols-outlined text-[20px]">grid_view</span>
              </span>
            </button>
          )}

          {showFocusHint && !isFocusedMode && tileIds.length >= 2 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 px-3.5 py-1.5 rounded-full bg-black/60 backdrop-blur-sm text-white text-[12px] font-medium pointer-events-none shadow-lg animate-fade-in whitespace-nowrap">
              {t('call.group_double_tap_hint', '두 번 탭하면 크게 볼 수 있어요')}
            </div>
          )}
        </div>

        <div className="mt-4 grid grid-cols-5 items-start">
          <div className="flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={handleToggleMute}
              className={`w-11 h-11 rounded-full flex flex-col items-center justify-center active:scale-95 transition-all cursor-pointer ${
                call.isMuted
                  ? 'bg-[#006565] text-white'
                  : 'bg-[#006565]/10 text-[#006565] hover:bg-[#006565]/20'
              }`}
              aria-label={t('call.mute')}
            >
              <span className="material-symbols-outlined text-[19px] leading-none mb-0.5">
                {call.isMuted ? 'mic_off' : 'mic'}
              </span>
              <span className="text-[9px] font-bold leading-none select-none">
                {call.isMuted ? 'OFF' : 'ON'}
              </span>
            </button>
            <span className="text-[11px] whitespace-nowrap leading-tight text-[#3e4949]">{t('call.mute')}</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={handleToggleCamera}
              className={`w-11 h-11 rounded-full flex flex-col items-center justify-center active:scale-95 transition-all cursor-pointer ${
                call.cameraOn
                  ? 'bg-[#006565] text-white'
                  : 'bg-[#006565]/10 text-[#006565] hover:bg-[#006565]/20'
              }`}
              aria-label={t('call.video')}
            >
              <span className="material-symbols-outlined text-[19px] leading-none mb-0.5">videocam</span>
              <span className="text-[9px] font-bold leading-none select-none">
                {call.cameraOn ? 'ON' : 'OFF'}
              </span>
            </button>
            <span className="text-[11px] whitespace-nowrap leading-tight text-[#3e4949]">{t('call.video')}</span>
          </div>
          {outputRole === 'speaker' ? (
            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={handleSpeakerClick}
                disabled={isSwitchingAudio}
                className={`w-11 h-11 rounded-full flex items-center justify-center active:scale-95 transition-all cursor-pointer disabled:opacity-50 ${
                  isSpeakerToggled
                    ? 'bg-[#006565] text-white'
                    : 'bg-[#006565]/10 text-[#006565] hover:bg-[#006565]/20'
                }`}
                aria-label={t('call.speaker')}
              >
                <span className="material-symbols-outlined text-[22px]">volume_up</span>
              </button>
              <span className="text-[11px] whitespace-nowrap leading-tight text-[#3e4949]">{t('call.speaker')}</span>
            </div>
          ) : outputRole === 'reconnect' ? (
            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={handleReconnectAudioClick}
                disabled={isSwitchingAudio}
                className="w-11 h-11 rounded-full flex items-center justify-center active:scale-95 transition-all cursor-pointer bg-[#006565]/10 text-[#006565] hover:bg-[#006565]/20 disabled:opacity-50"
                aria-label={t('call.earphones')}
              >
                <span className="material-symbols-outlined text-[22px]">headphones</span>
              </button>
              <span className="text-[11px] whitespace-nowrap leading-tight text-[#3e4949]">{t('call.earphones')}</span>
            </div>
          ) : null}
          <div className="flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={handleTranslateClick}
              className="w-11 h-11 rounded-full flex items-center justify-center active:scale-95 transition-all cursor-pointer bg-[#006565]/5 text-[#006565]/40 hover:bg-[#006565]/10"
              aria-label={t('call.translate')}
              title={t('call.translate')}
            >
              <span className="material-symbols-outlined text-[22px]">translate</span>
            </button>
            <span className="text-[11px] whitespace-nowrap leading-tight text-[#3e4949]">{t('call.translate')}</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={handleHangUp}
              className="w-12 h-12 rounded-full flex items-center justify-center bg-[#ba1a1a] hover:bg-[#93000a] text-white active:scale-95 transition-all cursor-pointer"
              aria-label={t('call.end')}
            >
              <span className="material-symbols-outlined text-[26px]">call_end</span>
            </button>
            <span className="text-[11px] whitespace-nowrap leading-tight text-[#3e4949]">{t('call.end')}</span>
          </div>
        </div>
      </div>

      {isMicGuideOpen && call.phase === 'active' && (
        <div
          className="fixed inset-0 z-[110] bg-black/40 flex items-center justify-center p-4"
          onClick={() => setIsMicGuideOpen(false)}
        >
          <div
            className="bg-white rounded-2xl p-4 w-full max-w-[340px] shadow-xl text-left"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3 border-b border-[#006565]/20 pb-2">
              <div className="flex items-center gap-1.5 text-[#006565] font-bold text-[14px]">
                <span className="material-symbols-outlined text-[19px]">mic_off</span>
                <span>{t('call.mic_guide_title')}</span>
              </div>
              <button
                type="button"
                onClick={() => setIsMicGuideOpen(false)}
                className="w-7 h-7 rounded-full flex items-center justify-center text-[#6e7979] hover:bg-black/5 active:scale-90 transition-all cursor-pointer"
                aria-label={t('call.mic_guide_close_aria')}
              >
                <span className="material-symbols-outlined text-[18px]">close</span>
              </button>
            </div>

            <div className="text-[12px] text-[#333333] space-y-2.5 leading-snug pr-0.5">
              <div className="flex items-start gap-1.5">
                <span className="font-bold text-[#006565] flex-shrink-0">1.</span>
                <span>{t('call.mic_guide_step1')}</span>
              </div>
              <div className="flex items-start gap-1.5">
                <span className="font-bold text-[#006565] flex-shrink-0">2.</span>
                <span>{t('call.mic_guide_step2')}</span>
              </div>
              <div className="flex items-start gap-1.5">
                <span className="font-bold text-[#006565] flex-shrink-0">3.</span>
                <span>{t('call.mic_guide_step3')}</span>
              </div>
              <div className="flex items-start gap-1.5">
                <span className="font-bold text-[#006565] flex-shrink-0">4.</span>
                <span>{t('call.mic_guide_step4')}</span>
              </div>
            </div>

            <div className="mt-4 pt-2 border-t border-black/10">
              <button
                type="button"
                onClick={() => setIsMicGuideOpen(false)}
                className="w-full py-2 rounded-full bg-[#006565] text-white font-semibold text-[13px] hover:bg-[#005050] active:scale-95 transition-all cursor-pointer text-center shadow-md"
              >
                {t('call.mic_guide_close_btn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
